<template>
  <!-- 框框加文字 -->
  <div class="id_2" :style="{ paddingTop: (oDivPaddingTop + 30) + 'px' }">
    <div class="o_div" :style="{ paddingTop: (oDivPaddingTop + 10) + 'px' }">
      <!-- 统一的插入功能组件 -->
      <ModuleInserter
        ref="moduleInserter"
        :features="insertFeatures"
        :target-ref.sync="$refs.concent"
        @image-upload="handleImageInsert"
        @component-inserted="handleComponentInserted"
      />

      <div class="top cursor clear" ref="topDiv">
        <div class="img_box fl" v-if="data.src">
          <img :src="data.src">
        </div>

        <div class="title bold fl" v-html="data.title"></div>
      </div>

      <div class="concent">
        <div class="text" ref="concent" v-html="data.text"></div>
      </div>
    </div>

  </div>
</template>

<script>
/**
 * 框框加文字组件 - id_2
 *
 * 功能：
 * 1. 展示标题、封面图与正文内容
 * 2. 支持向正文插入图片 / 高亮 / 分栏 / 表格 / 文件
 * 3. 根据顶部区域高度自适应外层 padding
 * 4. 通过 getFullData 导出自身与子组件完整数据
 */
import ModuleInserter from './ModuleInserter.vue'

export default {
  name: 'id_2',
  props: {
    data: Object
  },
  components: {
    ModuleInserter
  },
  data () {
    return {
      topDivHeight: 0,
      resizeObserver: null,
      insertFeatures: ['image', 'highlight', 'columns', 'table', 'file']
    }
  },
  computed: {
    oDivPaddingTop () {
      return this.topDivHeight / 2
    }
  },
  methods: {
    /**
     * 获取组件完整数据（包括自身数据和插入的子组件数据）
     * @returns {Object} 完整的组件数据
     */
    getFullData () {
      const result = {
        id: 2,
        title: (this.data && this.data.title) || '',
        text: null,
        src: (this.data && this.data.src) || '',
        components: []
      }

      // 构建 DOM 到组件实例的映射
      const domToInstanceMap = {}
      if (this.$refs.moduleInserter) {
        const insertedComponents = this.$refs.moduleInserter.getInsertedComponentsData()
        insertedComponents.forEach(item => {
          if (item.dom) {
            domToInstanceMap[item.dom] = item
          }
        })
      }

      // 遍历文本区域的子节点，按顺序收集文本和组件数据
      if (this.$refs.concent) {
        const children = this.$refs.concent.children
        for (let i = 0; i < children.length; i++) {
          const child = children[i]

          // 跳过空段落（组件插入时创建的占位符）且没有内容
          if (child.classList && child.classList.contains('empty-text-paragraph') && !child.textContent) {
            continue
          }

          const componentId = this.getChildComponentId(child)

          if (componentId && componentId >= 10 && componentId <= 13) {
            const componentItem = domToInstanceMap[child]
            if (componentItem && componentItem.componentInstance) {
              const componentInstance = componentItem.componentInstance

              // 如果子组件有 saveState 方法，先调用以确保数据同步
              if (typeof componentInstance.saveState === 'function') {
                componentInstance.saveState()
              }

              const componentData = this.buildComponentData(componentId, componentInstance.data, child)
              if (componentData) {
                result.components.push({
                  type: 'component',
                  ...componentData
                })
              }
            }
          } else {
            // 文本元素：包装为文本项
            const textContent = child.innerHTML
            if (textContent && textContent.trim() !== '' && textContent !== '<br>') {
              result.components.push({
                type: 'text',
                content: textContent
              })
            }
          }
        }
      }

      return result
    },

    /**
     * 从子节点 class 中解析组件数字 ID（如 id_11 -> 11）
     * @param {HTMLElement} child
     * @returns {number|null}
     */
    getChildComponentId (child) {
      const className = (child.className || '').toString()
      if (!className) return null
      const tokens = className.split(/\s+/)
      for (let j = 0; j < tokens.length; j++) {
        const token = tokens[j]
        if (token.indexOf('id_') === 0) {
          const id = parseInt(token.replace('id_', ''), 10)
          return isNaN(id) ? null : id
        }
      }
      return null
    },

    /**
     * 构建子组件的返回数据
     * @param {Number} id - 组件ID
     * @param {Object} propsData - 组件的props数据
     * @param {HTMLElement} dom - 组件的DOM元素
     * @returns {Object} 组件数据
     */
    buildComponentData (id, propsData, dom) {
      propsData = propsData || {}
      switch (id) {
        case 13: // 高亮块
          return {
            id: 13,
            text: propsData.text || (dom ? (dom.querySelector('.highlight-content') || {}).innerHTML || '' : ''),
            bgColor: propsData.bgColor || '',
            borderColor: propsData.borderColor || ''
          }
        case 11: // 分栏
          return {
            id: 11,
            columns: propsData.columns || []
          }
        case 12: // 表格
          return {
            id: 12,
            table: propsData.table || [],
            rowBgColors: propsData.rowBgColors || {},
            colBgColors: propsData.colBgColors || {},
            cellBgColors: propsData.cellBgColors || {},
            isFullWidth: propsData.isFullWidth || false,
            hasHeaderRow: propsData.hasHeaderRow || false,
            hasHeaderCol: propsData.hasHeaderCol || false,
            colWidths: propsData.colWidths || [],
            rowHeights: propsData.rowHeights || [],
            mergedCells: propsData.mergedCells || []
          }
        case 10: { // 文件
          // id_10 的 list 存储为 JSON 字符串，需要解析
          let listData = propsData.list
          if (typeof listData === 'string') {
            try {
              listData = JSON.parse(listData) || []
            } catch (e) {
              listData = []
            }
          }
          return {
            id: 10,
            list: listData || []
          }
        }
        default:
          return null
      }
    },

    /**
     * 打开插入功能面板
     */
    openInsertPanel () {
      this.$refs.moduleInserter.openPanel()
    },

    /**
     * 处理图片插入事件（由ModuleInserter组件触发）
     */
    handleImageInsert () {
      this.upImg()
    },

    /**
     * 处理组件插入完成事件（由ModuleInserter组件触发）
     */
    handleComponentInserted () {
      this.$nextTick(() => {
        this.updateTopDivHeight()
      })
    },

    /**
     * 从数据中恢复子组件和文本内容
     * @param {Array} components - 混合内容数组（包含文本和组件）
     */
    initComponents (components) {
      if (!components || !Array.isArray(components) || components.length === 0) {
        return
      }

      const textEl = this.$refs.concent
      if (!textEl) return

      // 组件类型映射
      const typeMap = {
        13: 'highlight',
        11: 'columns',
        12: 'table',
        10: 'file'
      }

      // 先销毁已有动态组件，再重建 DOM，避免实例残留
      if (this.$refs.moduleInserter) {
        this.$refs.moduleInserter.destroyComponents()
      }

      // 先清空内容
      textEl.innerHTML = ''

      // 第一阶段：构建DOM结构，文本直接渲染，组件创建占位符
      const componentPlaceholders = []
      components.forEach((item, index) => {
        if (item.type === 'text') {
          const p = document.createElement('p')
          p.innerHTML = item.content || ''
          textEl.appendChild(p)
        } else if (item.type === 'html') {
          // 使用 insertAdjacentHTML 追加，避免破坏已添加的节点引用
          textEl.insertAdjacentHTML('beforeend', item.content || '')
        } else if (item.type === 'component') {
          const placeholder = document.createElement('div')
          placeholder.setAttribute('data-component-index', index)
          textEl.appendChild(placeholder)
          componentPlaceholders.push({
            placeholder,
            compData: item
          })
        }
      })

      // 第二阶段：替换占位符为实际Vue组件
      this.$nextTick(() => {
        componentPlaceholders.forEach(({ placeholder, compData }) => {
          const type = typeMap[compData.id]
          if (type && this.$refs.moduleInserter) {
            this.$refs.moduleInserter.insertVueComponentAt(type, compData, placeholder)
          }
        })

        this.$nextTick(() => {
          this.updateTopDivHeight()
        })
      })
    },

    /**
     * 上传图片并插入到内容区域末尾
     */
    upImg () {
      if (!this.$api || !this.$api.upImg) {
        return
      }
      this.$api.upImg().then((url) => {
        if (!this.$refs.concent) return
        const str = `<img class="img_id2" src="${url}"><div class="img_title">(图片介绍，不需要可自行删除)</div>`
        this.$refs.concent.innerHTML = this.$refs.concent.innerHTML + str
        this.$nextTick(() => {
          this.updateTopDivHeight()
        })
      })
    },

    updateTopDivHeight () {
      if (this.$refs.topDiv) {
        const rect = this.$refs.topDiv.getBoundingClientRect()
        this.topDivHeight = rect.height
      }
    },

    setupResizeObserver () {
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === this.$refs.topDiv) {
              this.topDivHeight = entry.contentRect.height
            }
          }
        })
        if (this.$refs.topDiv) {
          this.resizeObserver.observe(this.$refs.topDiv)
        }
      }
    }
  },
  watch: {
    'data.title': {
      immediate: false,
      handler () {
        this.$nextTick(() => {
          this.updateTopDivHeight()
        })
      }
    },
    'data.components': {
      immediate: false,
      handler (newVal) {
        // 当components变化时，重新初始化组件
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          this.$nextTick(() => {
            this.initComponents(newVal)
          })
        }
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.moduleInserter && this.$refs.concent) {
        this.$refs.moduleInserter.setTargetRef(this.$refs.concent)
      }
      this.updateTopDivHeight()
      this.setupResizeObserver()
      // 从数据中恢复子组件
      if (this.data && this.data.components && Array.isArray(this.data.components)) {
        this.initComponents(this.data.components)
      }
    })
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    // 销毁所有动态创建的组件
    if (this.$refs.moduleInserter) {
      this.$refs.moduleInserter.destroyComponents()
    }
  }
}
</script>

<style lang="scss">
.id_2{
  .text{
    font-size: 16px;
    line-height: 26px;
    color: #333;
    word-wrap:break-word;
    .img_id2{
      width: 100%;
      max-width: 100%;
      margin-top: 24px;
    }
    .img_title{
      font-size: 14px;
      line-height: 20px;
      text-align: center;
      color: #909399;
      margin-top: 16px;
    }
  }
}
</style>

<style lang="scss" scoped>
.id_2{
  padding-top: 40px;
  background-color: #fff;
  .o_div{
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 24px;
    position: relative;
    .top{
      max-width: 94%;
      display: flex;
      align-items: center;
      position: absolute;
      background: #fff;
      padding: 0 8px;
      top: 0;
      transform: translateY(-50%);
      left: 16px;
      .img_box{
        width: 30px;
        height: 30px;
        img{
          width: 30px;
          height: 30px;
        }
      }
      .title{
        flex: 1;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 20px;
        line-height: 28px;
        padding-left: 8px;
        color: #303133;
      }
    }
    .text{
      white-space: pre-wrap;
      word-break: break-word;
      font-size: 16px;
      line-height: 26px;
      color: #333;
      /deep/ .id_10, /deep/ .id_11, /deep/ .id_12, /deep/ .id_13{
        padding: 0;
        padding-top: 12px;
      }
      img{
        width: 100%;
        margin-top: 24px;
        margin-bottom: 24px;
      }
      // 确保插入到contenteditable内部的Vue组件不可编辑
      > div[contenteditable="false"]{
        margin-top: 16px;
        margin-bottom: 16px;
        outline: none;
      }
      // 组件后自动插入的空段落样式
      > p.empty-text-paragraph{
        margin: 0;
        padding: 0;
        min-height: 1.6em;
        line-height: 1.6;
        font-size: 16px;
        color: #333;
        outline: none;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/module/course/public/id_2.vue